// src/utils/documentProperties.js
// Helper functions to store and retrieve citation data in Word document custom properties

const SUPERSCRIPT_TO_DIGIT = {
  '⁰': '0', '¹': '1', '²': '2', '³': '3', '⁴': '4',
  '⁵': '5', '⁶': '6', '⁷': '7', '⁸': '8', '⁹': '9',
};

/**
 * Extract citation numbers from an inserted_text string, handling both
 * regular digits ("(1, 2)", "[3]") and AMA superscript Unicode ("¹²").
 */
export function extractCitationNumbers(text) {
  if (!text) return [];
  const normalized = text.replace(/[⁰¹²³⁴⁵⁶⁷⁸⁹]/g, (ch) => SUPERSCRIPT_TO_DIGIT[ch]);
  const matches = normalized.match(/\d+/g) || [];
  return matches.map((n) => parseInt(n, 10)).filter((n) => !isNaN(n));
}

function getArticleIds(citation) {
  if (citation.article_ids && Array.isArray(citation.article_ids)) {
    return citation.article_ids;
  }
  return citation.article_id ? [citation.article_id] : [];
}

/**
 * Get stored citation mapping from document
 * @returns {Promise<{citations: Array, style: string}>}
 */
export async function getStoredCitationMapping() {
  return Word.run(async (context) => {
    try {
      const properties = context.document.properties.customProperties;
      properties.load("items");
      await context.sync();

      // Find the citemed_citations property
      const item = properties.items.find(p => p.key === "citemed_citations");

      if (item) {
        context.load(item, "value");
        await context.sync();
        return JSON.parse(item.value);
      }

      // Return empty mapping if not found
      return { citations: [], style: "apa" };
    } catch (error) {
      return { citations: [], style: "apa" };
    }
  });
}

/**
 * Save citation mapping to document
 * @param {Array} citationArray - Array of citation groups
 * @param {string} style - Citation style (apa, vancouver, etc.)
 * @param {Array} baseCitationArray - Optional array of base citations in Chicago format
 */
export async function saveCitationMapping(citationArray, style, baseCitationArray = null) {
  return Word.run(async (context) => {
    try {
      const data = { citations: citationArray, style: style };
      if (baseCitationArray) {
        data.base_citations = baseCitationArray;
      }

      const properties = context.document.properties.customProperties;
      properties.load("items");
      await context.sync();

      // Delete old property if it exists
      const oldProp = properties.items.find(p => p.key === "citemed_citations");
      if (oldProp) {
        oldProp.delete();
        await context.sync();
      }

      // Add new property
      properties.add("citemed_citations", JSON.stringify(data));
      await context.sync();
    } catch (error) {
      throw error;
    }
  });
}

/**
 * Add new citations to the stored mapping
 * @param {Array} newCitations - Array of {article_id, number, group_id, inserted_text} objects
 * @param {string} currentStyle - Current citation style
 * @param {Array} baseCitations - Array of base citations in Chicago format (same structure as newCitations)
 */
export async function addToStoredMapping(newCitations, currentStyle, baseCitations = null) {
  const existing = await getStoredCitationMapping();
  
  // Group new citations by group_id
  const newGroups = {};
  for (const cit of newCitations) {
    const groupId = cit.group_id || `g${Date.now()}_${Math.random()}`;
    if (!newGroups[groupId]) {
      newGroups[groupId] = {
        article_ids: [],
        group_id: groupId,
        inserted_text: cit.inserted_text
      };
    }
    if (cit.article_id && !newGroups[groupId].article_ids.includes(cit.article_id)) {
      newGroups[groupId].article_ids.push(cit.article_id);
    }
  }
  
  // Group base citations by group_id (if provided)
  const baseGroups = {};
  if (baseCitations) {
    for (const cit of baseCitations) {
      const groupId = cit.group_id || `g${Date.now()}_${Math.random()}`;
      if (!baseGroups[groupId]) {
        baseGroups[groupId] = {
          article_ids: [],
          group_id: groupId,
          inserted_text: cit.inserted_text
        };
      }
      if (cit.article_id && !baseGroups[groupId].article_ids.includes(cit.article_id)) {
        baseGroups[groupId].article_ids.push(cit.article_id);
      }
    }
  }
  
  // Calculate count for each group - count of unique citation numbers in inserted_text
  // For numbered styles (vancouver, ieee, ama): extract numbers from inserted_text
  // For author-date styles (chicago, apa, etc.): use article_ids length
  const newGroupEntries = Object.values(newGroups).map(group => {
    const uniqueNumbers = [...new Set(extractCitationNumbers(group.inserted_text))];

    // If we found valid numbers, use that count (numbered styles)
    // Otherwise, use article_ids length (author-date styles)
    const count = uniqueNumbers.length > 0 ? uniqueNumbers.length : group.article_ids.length;

    return {
      ...group,
      count: count
    };
  });
  
  // Calculate count for base groups (if provided)
  // Base citations are always in Chicago format (author-date style)
  // So count should be based on article_ids length, not numbers in text
  const baseGroupEntries = baseCitations ? Object.values(baseGroups).map(group => {
    // For Chicago format (author-date), count is based on article_ids
    return {
      ...group,
      count: group.article_ids.length
    };
  }) : [];
  
  // Dedupe by article_id: if any article_id in a new entry already exists in
  // the stored mapping, skip the whole entry. This prevents the same article
  // from accumulating multiple entries when re-cited.
  const existingArticleIds = new Set();
  existing.citations.forEach((c) => {
    getArticleIds(c).forEach((id) => existingArticleIds.add(id));
  });

  const toAdd = newGroupEntries.filter((c) =>
    !c.article_ids.some((id) => existingArticleIds.has(id))
  );

  const updated = [...existing.citations, ...toAdd];

  // Update base_citations if provided (same dedup strategy)
  let updatedBaseCitations = existing.base_citations || [];
  if (baseCitations && baseGroupEntries.length > 0) {
    const baseExistingArticleIds = new Set();
    (existing.base_citations || []).forEach((c) => {
      getArticleIds(c).forEach((id) => baseExistingArticleIds.add(id));
    });

    const baseToAdd = baseGroupEntries.filter((c) =>
      !c.article_ids.some((id) => baseExistingArticleIds.has(id))
    );

    updatedBaseCitations = [...updatedBaseCitations, ...baseToAdd];
  }
  
  await saveCitationMapping(updated, currentStyle, updatedBaseCitations);

  return { citations: updated, style: currentStyle, base_citations: updatedBaseCitations };
}

/**
 * Get count of unique article_ids from stored mapping
 * This represents how many unique articles have been cited
 * @returns {Promise<number>} Count of unique article_ids
 */
export async function getUniqueArticleIdsCount() {
  const mapping = await getStoredCitationMapping();
  
  if (!mapping.citations || mapping.citations.length === 0) {
    return 0;
  }
  
  // Collect all unique article_ids
  const uniqueArticleIds = new Set();
  mapping.citations.forEach(cit => {
    if (cit.article_ids && Array.isArray(cit.article_ids)) {
      cit.article_ids.forEach(id => uniqueArticleIds.add(id));
    } else if (cit.article_id) {
      // Handle legacy format
      uniqueArticleIds.add(cit.article_id);
    }
  });
  
  return uniqueArticleIds.size;
}

/**
 * Get mapping of article_id to citation number from stored mapping
 * @returns {Promise<Map<number, number>>} Map of article_id to citation number
 */
export async function getArticleIdToNumberMap() {
  const mapping = await getStoredCitationMapping();
  return buildArticleIdToNumberMap(mapping);
}

const NUMBERED_STYLES = ['vancouver', 'ieee', 'ama', 'critical-care'];

/**
 * Build article_id -> citation number map from an already-loaded mapping.
 * Exposed for callers that need the map in the same sync context.
 *
 * For numbered styles (Vancouver/IEEE/AMA/Critical-Care), digits embedded in
 * inserted_text ARE the citation numbers. For author-date styles (APA,
 * Chicago, ...), inserted_text contains years instead — we assign sequential
 * numbers based on the order articles appear in the mapping instead.
 */
export function buildArticleIdToNumberMap(mapping) {
  const articleIdToNumber = new Map();
  if (!mapping || !mapping.citations) return articleIdToNumber;

  const isNumberedStyle = mapping.style && NUMBERED_STYLES.includes(mapping.style.toLowerCase());

  if (isNumberedStyle) {
    mapping.citations.forEach((cit) => {
      if (!cit.inserted_text) return;
      const articleIds = getArticleIds(cit);
      if (articleIds.length === 0) return;

      const uniqueSorted = [...new Set(extractCitationNumbers(cit.inserted_text))].sort((a, b) => a - b);
      articleIds.forEach((articleId, idx) => {
        if (idx < uniqueSorted.length && !articleIdToNumber.has(articleId)) {
          articleIdToNumber.set(articleId, uniqueSorted[idx]);
        }
      });
    });
  } else {
    let nextNumber = 1;
    mapping.citations.forEach((cit) => {
      const articleIds = getArticleIds(cit);
      articleIds.forEach((articleId) => {
        if (!articleIdToNumber.has(articleId)) {
          articleIdToNumber.set(articleId, nextNumber++);
        }
      });
    });
  }

  return articleIdToNumber;
}

/**
 * Get maximum citation number from stored mapping
 * Extracts all citation numbers from inserted_text values and finds the maximum
 * @returns {Promise<number>} Maximum citation number
 */
export async function getMaxCitationNumber() {
  const mapping = await getStoredCitationMapping();
  if (!mapping.citations || mapping.citations.length === 0) return 0;

  let max = 0;
  mapping.citations.forEach((cit) => {
    extractCitationNumbers(cit.inserted_text).forEach((n) => {
      if (n > max) max = n;
    });
  });
  return max;
}

/**
 * Clear all citation data from document
 */
export async function clearCitationMapping() {
  return Word.run(async (context) => {
    try {
      const properties = context.document.properties.customProperties;
      properties.load("items");
      await context.sync();

      const item = properties.items.find(p => p.key === "citemed_citations");
      if (item) {
        item.delete();
        await context.sync();
      }
    } catch (error) {
      // ignore
    }
  });
}

/**
 * Validate and clean up stale citations that no longer exist in the document
 * @returns {Promise<{removed: number, remaining: number}>}
 */
export async function validateAndCleanCitations() {
  const mapping = await getStoredCitationMapping();

  if (!mapping.citations || mapping.citations.length === 0) {
    return { removed: 0, remaining: 0 };
  }

  const originalCount = mapping.citations.length;

  const validatedCitations = await Word.run(async (context) => {
    const body = context.document.body;
    context.load(body, 'text');
    await context.sync();

    const documentText = body.text;

    const textOccurrences = new Map();
    const textToGroups = new Map();

    mapping.citations.forEach(cit => {
      if (!cit.inserted_text) return;

      if (!textOccurrences.has(cit.inserted_text)) {
        let count = 0;
        let searchIndex = 0;
        while (true) {
          const pos = documentText.indexOf(cit.inserted_text, searchIndex);
          if (pos === -1) break;
          count++;
          searchIndex = pos + 1;
        }
        textOccurrences.set(cit.inserted_text, count);
      }

      if (!textToGroups.has(cit.inserted_text)) {
        textToGroups.set(cit.inserted_text, []);
      }
      textToGroups.get(cit.inserted_text).push(cit);
    });

    const validCitations = [];

    for (const [text, groups] of textToGroups) {
      const occurrenceCount = textOccurrences.get(text) || 0;

      if (occurrenceCount === 0) {
        // Citation text no longer present in the document — drop it.
        continue;
      }

      if (groups.length <= occurrenceCount) {
        validCitations.push(...groups);
      } else {
        // More mapping entries than occurrences — keep the newest by group_id timestamp.
        const sortedGroups = groups.sort((a, b) => {
          const timestampA = parseInt(a.group_id.split('_')[1]) || 0;
          const timestampB = parseInt(b.group_id.split('_')[1]) || 0;
          return timestampB - timestampA;
        });

        validCitations.push(...sortedGroups.slice(0, occurrenceCount));
      }
    }

    return validCitations;
  });

  const removedCount = originalCount - validatedCitations.length;

  if (removedCount > 0) {
    await saveCitationMapping(validatedCitations, mapping.style, mapping.base_citations || null);
  }

  return { removed: removedCount, remaining: validatedCitations.length };
}

